import { SearchInput } from '@/app/components/SearchInput';
import { Transition } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

interface TypeaheadProps {
  onSearch: (value: string) => void;
  onSelectionChange: (selected: any) => void;
  value: string;
  data: any[];
  render: (datum: any) => React.ReactElement<any>;
  placeholder?: string;
  isLoading?: boolean;
}

export const Typeahead: React.FC<TypeaheadProps> = ({
  onSearch,
  onSelectionChange,
  value,
  data,
  render,
  placeholder,
  isLoading = false,
}) => {
  const [isResultsOpen, setIsResultsOpen] = useState<boolean>(false);

  const handleSearch = (term: string) => {
    if (term.length > 0) {
      setIsResultsOpen(true);
    } else {
      setIsResultsOpen(false);
    }
    onSearch(term);
  };

  const handleSelection = selectedDatum => {
    onSelectionChange(selectedDatum);
    setIsResultsOpen(false);
  };

  const handleFocus = eventTarget => {
    if (document.activeElement === eventTarget && eventTarget.value.length > 0) {
      setIsResultsOpen(true);
    }
  };

  return (
    <div className="relative">
      <SearchInput
        onChange={handleSearch}
        isClearable={true}
        value={value}
        placeholder={placeholder || 'Search...'}
        onFocus={handleFocus}
      />
      <Transition show={isResultsOpen}>
        <div className="absolute left-0 right-0 z-10 mt-2 flex max-h-[300px] flex-col gap-3 overflow-y-scroll rounded-lg border border-gray-200 bg-white p-4">
          {isLoading ? (
            <>
              <div className="h-12 animate-pulse rounded-lg bg-gray-100" />
              <div className="h-12 animate-pulse rounded-lg bg-gray-100" />
            </>
          ) : data.length === 0 ? (
            <div className="m-auto flex h-full text-gray-500">
              <div className="m-auto">
                <MagnifyingGlassIcon className="mx-auto size-6" />
                <p className="py-2">No results found</p>
              </div>
            </div>
          ) : (
            <>
              {data.map(datum => (
                <button key={datum.id} onClick={() => handleSelection(datum)}>
                  <div className="flex flex-col justify-between overflow-hidden rounded-lg bg-gray-50 px-3 py-2 text-left ">
                    <div className="flex w-11/12 flex-col gap-1">{render(datum)}</div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </Transition>
    </div>
  );
};
